import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { OnlineOfflineStatus } from '@freelancer/datastore/collections/users';
import { IconColor, IconModule, IconSize } from '@freelancer/ui/icon';
import { OnlineIndicatorSize } from '@freelancer/ui/online-indicator';
import { OnlineIndicatorComponent } from '@freelancer/ui/online-indicator/online-indicator.component';
import {
  PictureComponent,
  PictureDisplay,
  PictureObjectFit,
} from '@freelancer/ui/picture';
import {
  VideoComponent,
  VideoModule,
  VideoObjectFit,
} from '@freelancer/ui/video';
import { isDefined } from '@freelancer/utils';
import { OnlineOfflineStatusApi } from 'api-typings/users/users';
import type { User } from './user-avatar.types';
import { AvatarSize, BackgroundColourType } from './user-avatar.types';
import { LoadingAvatarComponent } from '../loading-avatar';
import { ModalColor } from '../modal/modal-color';
import { ModalSize } from '../modal/modal-size';
import { ModalService } from '../modal/modal.service';
import { UserActivityStatusComponent } from '../user-activity-status';

@Component({
  standalone: true,
  imports: [
    IconModule,
    LoadingAvatarComponent,
    OnlineIndicatorComponent,
    PictureComponent,
    UserActivityStatusComponent,
    VideoModule,
  ],
  selector: 'fl-user-avatar',
  template: `
    @if (!isMulti) {
      <div
        class="UserAvatar"
        [class.UserAvatarVideo]="showVideoAvatar || showOnboardingModal"
        [class.UserAvatar-upsell]="showOnboardingModal"
        [attr.data-disable-avatar-border-radius-bottom]="
          disableAvatarBorderRadiousBottom
        "
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-online-size]="
          onlineIndicatorSize ?? calculatedOnlineIndicatorSize
        "
        [attr.data-online-size-tablet]="onlineIndicatorSizeTablet"
        [attr.data-online-size-desktop]="onlineIndicatorSizeDesktop"
        [attr.data-online-status]="
          isOnline !== undefined && size !== AvatarSize.XXSMALL
        "
        [attr.data-background-color]="backgroundColor"
        (click)="handleClick()"
        (mouseenter)="playVideo(true)"
        (mouseleave)="playVideo(false)"
      >
        @if (
          (!isAvatarImageReady && firstUserHasPic) || firstUser === undefined
        ) {
          <fl-loading-avatar
            [size]="size"
            [sizeTablet]="sizeTablet"
            [sizeDesktop]="sizeDesktop"
          />
        }
        @if (firstUser !== undefined) {
          @if (showVideoAvatar && videoSrc) {
            <fl-video
              #video
              class="UserAvatarPreviewVideo desktop:show"
              [src]="videoSrc"
              [disableControls]="true"
              [objectFit]="VideoObjectFit.COVER"
              [loop]="true"
            ></fl-video>
          }
          @if (firstUserHasPic) {
            <fl-picture
              class="UserAvatar-img"
              alt="User Avatar"
              i18n-alt="User avatar alternative text"
              title="{{ firstUser?.username }}"
              [class.UserAvatarVideo-img]="
                showVideoAvatar || showOnboardingModal
              "
              [src]="
                firstUser.avatarXLarge ??
                (shouldUseLargeAvatarForFirstUser
                  ? firstUser.avatarLarge
                  : firstUser?.avatar)
              "
              [display]="PictureDisplay.BLOCK"
              [objectFit]="PictureObjectFit.CONTAIN"
              [width]="
                shouldUseLargeAvatarForFirstUser
                  ? AVATAR_LARGE_WIDTH
                  : AVATAR_WIDTH
              "
              [height]="
                shouldUseLargeAvatarForFirstUser
                  ? AVATAR_LARGE_HEIGHT
                  : AVATAR_HEIGHT
              "
              [boundedWidth]="true"
              [boundedHeight]="true"
              [fullWidth]="true"
              (imageLoaded)="isAvatarImageReady = true"
            ></fl-picture>
          }
          @if (!firstUserHasPic) {
            <div
              class="UserAvatarLetter"
              [attr.data-background-color]="backgroundColor"
            >
              {{ firstUserLetter }}
            </div>
          }
        }
      </div>
    }

    @if (showVideoAvatar || showOnboardingModal) {
      <fl-icon
        class="UserAvatarVideo-playButton"
        [color]="IconColor.LIGHT"
        [label]="'user-avatar-play-icon'"
        [size]="IconSize.MID"
        [name]="'ui-play'"
        [useIconFont]="true"
        [fillIconFont]="true"
        (click)="handleClick()"
      ></fl-icon>
    }

    @if (
      !showActivityStatus &&
      isOnline !== undefined &&
      size !== AvatarSize.XXSMALL &&
      firstUser?.username &&
      !showVideoAvatar
    ) {
      <fl-online-indicator
        class="OnlineStatus"
        [border]="!disableOnlineIndicatorBorder"
        [username]="firstUser?.username"
        [isOnline]="isOnline"
        [size]="onlineIndicatorSize ?? calculatedOnlineIndicatorSize"
        [sizeTablet]="onlineIndicatorSizeTablet"
        [sizeDesktop]="onlineIndicatorSizeDesktop"
      ></fl-online-indicator>
    }

    @if (
      showActivityStatus &&
      (onlineStatus ||
        (disableLastActiveTimestampStatus && isOnline !== undefined)) &&
      size !== AvatarSize.XXSMALL &&
      firstUser?.username &&
      !showVideoAvatar
    ) {
      <fl-user-activity-status
        class="OnlineStatus"
        [disableLastActiveTimestampStatus]="disableLastActiveTimestampStatus"
        [isOnline]="
          onlineStatus
            ? onlineStatus.status === OnlineOfflineStatusApi.ONLINE
            : (isOnline ?? false)
        "
        [lastActiveTimestamp]="onlineStatus?.lastUpdatedTimestamp"
        [size]="onlineIndicatorSize ?? calculatedOnlineIndicatorSize"
        [sizeTablet]="onlineIndicatorSizeTablet"
        [sizeDesktop]="onlineIndicatorSizeDesktop"
        [username]="firstUser?.username"
      >
      </fl-user-activity-status>
    }

    @if (isMulti) {
      <div
        class="UserAvatar-multi"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-size-desktop]="sizeDesktop"
      >
        <div class="UserAvatar-multi-row">
          <div
            class="UserAvatar-content UserAvatar-content--topLeft"
            [attr.data-background-color]="backgroundColor"
          >
            @if (firstUserHasPic) {
              <fl-picture
                alt="User Avatar"
                i18n-alt="User avatar alternative text"
                class="UserAvatar-multi-img"
                title="{{ firstUser?.username }}"
                [src]="firstUser?.avatarXLarge ?? firstUser?.avatar"
                [display]="PictureDisplay.BLOCK"
                [objectFit]="PictureObjectFit.CONTAIN"
                [width]="AVATAR_WIDTH"
                [height]="AVATAR_HEIGHT"
                [boundedWidth]="true"
                [boundedHeight]="true"
                [fullWidth]="true"
              >
              </fl-picture>
            }
            @if (!firstUserHasPic) {
              <div
                class="UserAvatar-multi-letter"
                [attr.data-background-color]="backgroundColor"
                [attr.data-size]="size"
              >
                {{ firstUserLetter }}
              </div>
            }
          </div>
          <div
            class="UserAvatar-content UserAvatar-content--topRight"
            [attr.data-background-color]="backgroundColor"
          >
            @if (users.length > 2 && secondUserHasPic) {
              <fl-picture
                alt="User Avatar"
                i18n-alt="User avatar alternative text"
                class="UserAvatar-multi-img"
                title="{{ secondUser?.username }}"
                [src]="secondUser?.avatarXLarge ?? secondUser?.avatar"
                [display]="PictureDisplay.BLOCK"
                [objectFit]="PictureObjectFit.CONTAIN"
                [width]="AVATAR_WIDTH"
                [height]="AVATAR_HEIGHT"
                [boundedWidth]="true"
                [boundedHeight]="true"
                [fullWidth]="true"
              >
              </fl-picture>
            }
            @if (users.length > 2 && !secondUserHasPic) {
              <div
                class="UserAvatar-multi-letter"
                [attr.data-size]="size"
                [attr.data-background-color]="backgroundColor"
              >
                {{ secondUserLetter }}
              </div>
            }
          </div>
        </div>
        <div class="UserAvatar-multi-row UserAvatar-multi-row--secondary">
          <div
            class="UserAvatar-content UserAvatar-content--bottomLeft"
            [attr.data-background-color]="backgroundColor"
          >
            @if (users.length > 2 && thirdUserHasPic) {
              <fl-picture
                alt="User Avatar"
                i18n-alt="User avatar alternative text"
                class="UserAvatar-multi-img"
                title="{{ thirdUser?.username }}"
                [src]="thirdUser?.avatarXLarge ?? thirdUser?.avatar"
                [display]="PictureDisplay.BLOCK"
                [objectFit]="PictureObjectFit.CONTAIN"
                [width]="AVATAR_WIDTH"
                [height]="AVATAR_HEIGHT"
                [boundedWidth]="true"
                [boundedHeight]="true"
                [fullWidth]="true"
              >
              </fl-picture>
            }
            @if (users.length > 2 && !thirdUserHasPic) {
              <div
                class="UserAvatar-multi-letter"
                [attr.data-size]="size"
                [attr.data-background-color]="backgroundColor"
              >
                {{ thirdUserLetter }}
              </div>
            }
          </div>
          <div
            class="UserAvatar-content UserAvatar-content--bottomRight"
            [attr.data-background-color]="backgroundColor"
          >
            @if (bottomRightUser && bottomRightUserHasPic) {
              <fl-picture
                alt="User Avatar"
                i18n-alt="User avatar alternative text"
                class="UserAvatar-multi-img"
                title="{{ bottomRightUser.username }}"
                [src]="bottomRightUser?.avatarXLarge ?? bottomRightUser?.avatar"
                [display]="PictureDisplay.BLOCK"
                [objectFit]="PictureObjectFit.CONTAIN"
                [width]="AVATAR_WIDTH"
                [height]="AVATAR_HEIGHT"
                [boundedWidth]="true"
                [boundedHeight]="true"
                [fullWidth]="true"
              >
              </fl-picture>
            }
            @if (bottomRightUser && !bottomRightUserHasPic) {
              <div
                class="UserAvatar-multi-letter"
                [attr.data-size]="size"
                [attr.data-background-color]="backgroundColor"
              >
                {{ bottomRightUserLetter }}
              </div>
            }
            @if (!bottomRightUser && bottomRightText) {
              <div
                class="UserAvatar-multi-letter"
                [attr.data-size]="size"
              >
                {{ bottomRightText }}
              </div>
            }
          </div>
        </div>
      </div>
    }
  `,
  styleUrls: ['./user-avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserAvatarComponent implements OnChanges, OnInit {
  private modalService = inject(ModalService);

  AvatarSize = AvatarSize;
  IconColor = IconColor;
  IconSize = IconSize;
  OnlineOfflineStatusApi = OnlineOfflineStatusApi;
  PictureDisplay = PictureDisplay;
  PictureObjectFit = PictureObjectFit;
  VideoObjectFit = VideoObjectFit;

  @Input() users: readonly (User | undefined)[];

  @HostBinding('attr.data-size')
  @Input()
  size: AvatarSize = AvatarSize.MID;

  @HostBinding('attr.data-size-tablet')
  @Input()
  sizeTablet?: AvatarSize;

  @HostBinding('attr.data-size-desktop')
  @Input()
  sizeDesktop?: AvatarSize;

  // This input is not supported. We know everyone loves round avatars, but you can't use them.
  // There are corporate and group users who upload square logos that shouldn't be rounded.
  // Don't use custom CSS to achieve this effect. Violating diffs will be shot on sight.
  // @Input() isCircle? = false;

  @Input() isOnline?: boolean;
  /** Used with `showActivityStatus`. */
  @Input() onlineStatus?: OnlineOfflineStatus;
  /** Show the user activity status instead of the online indicator. */
  @Input() showActivityStatus?: boolean = false;
  /** Disable the user activity status from displaying the last active time. */
  @Input() disableLastActiveTimestampStatus?: boolean = false;

  firstUser?: User;
  firstUserLetter?: string;
  firstUserHasPic: boolean;
  secondUser?: User;
  secondUserLetter?: string;
  secondUserHasPic: boolean;
  thirdUser?: User;
  thirdUserLetter?: string;
  thirdUserHasPic: boolean;
  bottomRightUser?: User;
  bottomRightUserLetter?: string;
  bottomRightUserHasPic: boolean;
  bottomRightText?: string;
  isMulti: boolean;
  showVideoAvatar = false;

  /** The calculated size of the online indicator based on the avatar size. Will be used if the `onlineIndicatorSize` input is not provided. */
  calculatedOnlineIndicatorSize: OnlineIndicatorSize =
    this.getOnlineIndicatorSize(this.size);

  @Input()
  onlineIndicatorSize?: OnlineIndicatorSize;

  @Input()
  onlineIndicatorSizeTablet?: OnlineIndicatorSize;

  @Input()
  onlineIndicatorSizeDesktop?: OnlineIndicatorSize;

  @HostBinding('attr.data-is-video-avatar')
  @Input()
  isVideoAvatar?: boolean = false;

  @Input() videoSrc?: string;

  @Input() showOnboardingModal?: boolean = false;

  /** Disable the border radius bottom. */
  @Input() disableAvatarBorderRadiousBottom?: boolean = false;

  /** Disable the white border around the online indicator. */
  @Input() disableOnlineIndicatorBorder?: boolean = false;

  @Input() backgroundColor?: BackgroundColourType;

  @Input() showFourthImage = false;

  @ViewChild('video') video: VideoComponent;

  @Output() videoModalVideoPlayed = new EventEmitter<void>();
  @Output() videoModalVideoEnded = new EventEmitter<void>();

  readonly COUNT_LIMIT = 99;
  // Regular avatars from the backend are always 100x100
  readonly AVATAR_HEIGHT = 100;
  readonly AVATAR_WIDTH = 100;
  // Large avatars from the backend are usually 280x280.
  // Some are 280x279, but we could probably ignore the 1px difference
  // for the sake of not having to change the backend to include the
  // image dimensions in its response
  readonly AVATAR_LARGE_HEIGHT = 280;
  readonly AVATAR_LARGE_WIDTH = 280;

  isAvatarImageReady = false;
  shouldUseLargeAvatarForFirstUser = false;

  private hasPic(user?: User): user is User & { avatar: string } {
    return (
      isDefined(user) &&
      isDefined(user.avatar ?? user.avatarLarge ?? user.avatarXLarge)
    );
  }

  private getLetter(user?: User): string | undefined {
    // falsey check is OK because empty string should be undefined too.
    return !!user && !!user.username
      ? user.username[0].toUpperCase()
      : undefined;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('users' in changes) {
      const users = changes.users.currentValue;
      if (users) {
        // Check if the user avatar is for multiple users.
        this.isMulti = users.length > 1;
        [this.firstUser, this.secondUser, this.thirdUser] = users;
        this.firstUserHasPic = this.hasPic(this.firstUser);
        this.firstUserLetter = this.getLetter(this.firstUser);
        this.secondUserHasPic = this.hasPic(this.secondUser);
        this.secondUserLetter = this.getLetter(this.secondUser);
        this.thirdUserHasPic = this.hasPic(this.thirdUser);
        this.thirdUserLetter = this.getLetter(this.thirdUser);

        this.checkIfLargeAvatarForFirstUser();

        // Check for what to show for the bottom right user.
        if (this.showFourthImage) {
          // Always show the 4th user as an image if showFourthImage is true
          [, , , this.bottomRightUser] = users;
          this.bottomRightText = undefined;
        } else if (users.length === 2) {
          // Default behavior for 2 users
          [, this.bottomRightUser] = users;
          this.bottomRightText = undefined;
        } else if (users.length === 4) {
          [, , , this.bottomRightUser] = users;
          this.bottomRightText = undefined;
        } else if (users.length > 4) {
          const leftOverCount = users.length - 3;
          const count = Math.min(leftOverCount, this.COUNT_LIMIT);
          this.bottomRightText = `${count}+`;
          this.bottomRightUser = undefined;
        } else {
          this.bottomRightUser = undefined;
          this.bottomRightText = undefined;
        }

        this.bottomRightUserHasPic = this.hasPic(this.bottomRightUser);
        this.bottomRightUserLetter = this.getLetter(this.bottomRightUser);
      } else {
        this.isMulti = false;
        this.bottomRightUser = undefined;
        this.bottomRightText = undefined;
        this.bottomRightUserHasPic = false;
        this.bottomRightUserLetter = undefined;
      }
    }

    if ('size' in changes || 'showActivityStatus' in changes) {
      this.calculatedOnlineIndicatorSize = this.getOnlineIndicatorSize(
        this.size,
      );
      this.checkIfLargeAvatarForFirstUser();
    }

    if (
      ('sizeTablet' in changes || 'showActivityStatus' in changes) &&
      this.sizeTablet &&
      !this.onlineIndicatorSizeTablet
    ) {
      this.onlineIndicatorSizeTablet = this.getOnlineIndicatorSize(
        this.sizeTablet,
      );
    }

    if (
      ('sizeDesktop' in changes || 'showActivityStatus' in changes) &&
      this.sizeDesktop &&
      !this.onlineIndicatorSizeDesktop
    ) {
      this.onlineIndicatorSizeDesktop = this.getOnlineIndicatorSize(
        this.sizeDesktop,
      );
    }

    if (this.isMulti && this.isOnline !== undefined) {
      console.error(
        'Online indicator may only be displayed on a single user avatar',
      );
    }

    if ('videoSrc' in changes || 'isVideoAvatar' in changes) {
      this.showVideoAvatar = !!this.videoSrc && !!this.isVideoAvatar;
    }
  }

  ngOnInit(): void {
    this.calculatedOnlineIndicatorSize =
      this.onlineIndicatorSize || this.getOnlineIndicatorSize(this.size);
  }

  private getOnlineIndicatorSize(avatarSize: AvatarSize): OnlineIndicatorSize {
    if (this.showActivityStatus) {
      return OnlineIndicatorSize.ACTIVITY_STATUS;
    }

    switch (avatarSize) {
      case AvatarSize.MID:
        return OnlineIndicatorSize.MID;
      case AvatarSize.LARGE:
        return OnlineIndicatorSize.LARGE;
      case AvatarSize.XLARGE:
        return OnlineIndicatorSize.XXLARGE;
      case AvatarSize.XXLARGE:
        return OnlineIndicatorSize.MID;
      case AvatarSize.MAX:
        return OnlineIndicatorSize.XLARGE;
      default:
        return OnlineIndicatorSize.SMALL;
    }
  }

  private checkIfLargeAvatarForFirstUser(): void {
    this.shouldUseLargeAvatarForFirstUser =
      this.size === AvatarSize.MAX &&
      isDefined(this.firstUser?.avatarXLarge ?? this.firstUser?.avatarLarge);
  }

  playVideo(play: boolean): void {
    if (this.showVideoAvatar) {
      if (play) {
        this.video.play();
      } else {
        this.video.pause();
      }
    }
  }

  async openVideoModal(): Promise<void> {
    if (this.showVideoAvatar) {
      const {
        videoPlayed,
        videoEnded,
      }: {
        videoPlayed: boolean;
        videoEnded: boolean;
      } = await this.modalService
        .open('VideoModal', {
          inputs: {
            autoPlay: true,
            src: this.videoSrc,
            timeDisplay: false,
          },
          edgeToEdge: true,
          closeable: true,
          color: ModalColor.DARK,
        })
        .afterClosed();

      if (videoPlayed) {
        this.videoModalVideoPlayed.emit();
      }

      if (videoEnded) {
        this.videoModalVideoEnded.emit();
      }
    }
  }

  // FIXME: T271621 Move video bid-related logic to bid-card component
  openBidPitchOnboardingModal(): void {
    this.modalService.open('BidPitchOnboardingModal', {
      inputs: {
        isUpsell: true,
      },
      size: ModalSize.SMALL,
      closeable: true,
      mobileFullscreen: true,
    });
  }

  handleClick(): void {
    if (this.showOnboardingModal === true) {
      this.openBidPitchOnboardingModal();
    } else {
      this.openVideoModal();
    }
  }
}
